import React, { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import _first from 'lodash/first';
import _get from 'lodash/get';

// Components
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
// @ts-ignore
import Dropzone from 'react-dropzone';

interface Props {
  hasError: boolean;
  errorText: string | React.ReactNode;
  onFileChange: (file: any) => void;
  maxSize?: number;
  accept?: string;
  viewClassName?: string;
}

function FileDrop({ viewClassName, onFileChange, hasError, errorText, maxSize, accept }: Props) {
  const classes = useStyles();
  const [file, setFile] = useState(undefined);
  const [fileName, setFileName] = useState(null);

  const handleDrop = (files: any[]) => {
    const newFile = _first(files);
    const newFileName = _get(newFile, 'name', null);
    setFile(newFile);
    setFileName(newFileName);
    onFileChange(newFile);
  };

  return (
    <Dropzone
      className={classes.root}
      activeClassName={classes.rootDragOn}
      onDrop={handleDrop}
      maxSize={maxSize}
      accept={accept}
    >
      {!file && (
        <div className={classes.view}>
          <CloudUploadIcon className={classes.icon} />
          <div className={classes.lastFlexChild}>
            Click to upload file or drag and drop files in this area.
          </div>
        </div>
      )}
      {file && !hasError && (
        <div className={classes.view + ' ' + classes.success}>
          <CheckCircleIcon className={classes.icon} />
          <strong>{fileName}</strong>
          <div className={classes.lastFlexChild}>
            Click to select another file or drag and drop one into this area.
          </div>
        </div>
      )}
      {file && hasError && (
        <div className={classes.view + ' ' + classes.error}>
          <ErrorOutlineIcon className={classes.icon} />
          <strong>{fileName}</strong>
          <div className={classes.lastFlexChild}>{errorText}</div>
        </div>
      )}
      <div className={classes.overlay} />
    </Dropzone>
  );
}

const useStyles = makeStyles(theme => {
  return {
    root: {
      width: '100%',
      minHeight: 267,
      position: 'relative',
      color: '#8a8a8a',
      cursor: 'pointer',
      fontSize: 14,
      textAlign: 'center',
      overflow: 'hidden',
    },
    view: {
      minHeight: 267,
      backgroundColor: '#e2e2e2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      flexDirection: 'column',
      padding: 171,
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      '& > *:not(:last-child)': {
        marginBottom: theme.spacing(2),
      },
    },
    empty: {
      '&:hover $icon': {
        color: 'rgb(68, 138, 255)',
      },
    },
    success: {
      backgroundColor: '#e1f7f5',
      '& $icon': {
        color: '#00b59b',
      },
    },
    error: {
      backgroundColor: '#fdeaeb',
      color: '#f74f58',
    },
    rootDragOn: {
      '& $overlay': {
        display: 'block',
      },
    },
    overlay: {
      display: 'none',
      backgroundColor: '#84B1FF',
      opacity: '0.8',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    icon: {
      width: 84,
      height: 40,
    },
    input: {
      display: 'none',
    },
    lastFlexChild: {
      marginBottom: 0,
    },
  };
});

export default FileDrop;
